import { useState, useEffect, useRef } from 'react';
import { animate, stagger } from 'animejs';

const SettingsPanel = ({ flags, toggleFlag, resetToDefault }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const menuItemsRef = useRef([]);

    // Reset refs array on render
    menuItemsRef.current = [];

    const addToRefs = (el) => {
        if (el && !menuItemsRef.current.includes(el)) {
            menuItemsRef.current.push(el);
        }
    };

    useEffect(() => {
        if (isOpen) {
            // Animate In
            animate(dropdownRef.current, {
                opacity: [0, 1],
                translateY: [-10, 0],
                duration: 400,
                ease: 'out(1.56)',
                onBegin: () => {
                    if (dropdownRef.current) dropdownRef.current.style.pointerEvents = 'auto';
                }
            });

            animate(menuItemsRef.current, {
                opacity: [0, 1],
                translateX: [20, 0],
                delay: stagger(50, { start: 100 }),
                ease: 'out(2)'
            });
        } else {
            // Animate Out
            animate(dropdownRef.current, {
                opacity: 0,
                translateY: -10,
                duration: 300,
                ease: 'in(2)',
                onComplete: () => {
                    if (dropdownRef.current) dropdownRef.current.style.pointerEvents = 'none';
                }
            });
        }
    }, [isOpen]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key.toLowerCase() === 'k' && !e.metaKey && !e.ctrlKey && !e.altKey && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
                setIsOpen(prev => !prev);
            }
            if (e.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    const FEATURE_LABELS = {
        eventModals: 'Event Details',
        minimap: 'Minimap',
        zoomControls: 'Zoom Controls',
        quickJump: 'Quick Jump',
        autoPlay: 'Auto Play',
    };

    return (
        <div className="fixed top-6 right-6 z-40 flex flex-col items-end">
            {/* Toggle Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    w-8 h-8 flex items-center justify-center
                    text-white/50 hover:text-white transition-colors
                    ${isOpen ? 'text-white' : ''}
                `}
                aria-label="Toggle Settings"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {isOpen ? (
                        <>
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </>
                    ) : (
                        <>
                            <circle cx="12" cy="12" r="3" />
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                        </>
                    )}
                </svg>
            </button>

            {/* Dropdown Menu - Brutalist / No Container */}
            <div
                ref={dropdownRef}
                className="mt-4 flex flex-col items-end space-y-1"
                style={{ opacity: 0, pointerEvents: 'none' }} // Initial state
            >
                {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                    <button
                        key={key}
                        ref={addToRefs}
                        onClick={() => toggleFlag(key)}
                        className="group flex items-center gap-3 text-right"
                    >
                        <span className={`text-xs uppercase tracking-wider transition-colors ${flags[key] ? 'text-white' : 'text-white/30 group-hover:text-white/60'}`}>
                            {label}
                        </span>
                        <div className={`w-2 h-2 border border-current ${flags[key] ? 'bg-[#F5B50A] border-[#F5B50A]' : 'border-white/30'}`} />
                    </button>
                ))}

                <div ref={addToRefs} className="h-px w-8 bg-white/10 my-2" />

                <button
                    ref={addToRefs}
                    onClick={resetToDefault}
                    className="text-[10px] uppercase tracking-widest text-white/30 hover:text-red-500 transition-colors"
                >
                    Reset
                </button>
                <a
                    ref={addToRefs}
                    href="/classic"
                    className="text-[10px] uppercase tracking-widest text-white/30 hover:text-white transition-colors flex items-center gap-1"
                >
                    Classic
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                </a>
            </div>
        </div>
    );
};

export default SettingsPanel;
